import React, { useState, useEffect } from 'react'
import styles from './KibanaDashboard.module.scss'
import { config } from '../../config'

const KIBANA_URL = config.KIBANA_URL
const DASHBOARD_ID = config.KIBANA_DASHBOARD_ID

type TimeRange = 'today' | 'yesterday' | '3d' | '4d'

const KibanaDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>('today')
  const [refreshKey, setRefreshKey] = useState(0)
  const [showInfo, setShowInfo] = useState(() => localStorage.getItem('kibana_info_dismissed') !== 'true')

  const getTimeRangeParams = (range: TimeRange): { from: string; to: string } => {
    const now = new Date()
    let from: Date

    switch (range) {
      case 'today':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case 'yesterday':
        from = new Date(now.getTime() - 48 * 60 * 60 * 1000)
        break
      case '3d':
        from = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
        break
      case '4d':
        from = new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000)
        break
      default:
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    }

    return {
      from: from.toISOString(),
      to: now.toISOString(),
    }
  }

  const getEmbedUrl = (): string => {
    const { from, to } = getTimeRangeParams(timeRange)
    const timeParam = encodeURIComponent(`(from:'${from}',to:'${to}')`)
    const refreshInterval = encodeURIComponent('(pause:!t,value:60000)')
    return `${KIBANA_URL}/app/dashboards#/view/${DASHBOARD_ID}?embed=true&_g=(refreshInterval:${refreshInterval},time:${timeParam})&_a=()`
  }

  const getFullUrl = (): string => {
    const { from, to } = getTimeRangeParams(timeRange)
    const timeParam = encodeURIComponent(`(from:'${from}',to:'${to}')`)
    return `${KIBANA_URL}/app/dashboards#/view/${DASHBOARD_ID}?_g=(time:${timeParam})&_a=()`
  }

  useEffect(() => {
    setLoading(true)
    setError(false)
  }, [timeRange, refreshKey])

  const handleIframeLoad = () => {
    setLoading(false)
  }

  const handleIframeError = () => {
    setLoading(false)
    setError(true)
  }

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1)
  }

  const dismissInfo = () => {
    setShowInfo(false)
    localStorage.setItem('kibana_info_dismissed', 'true')
  }

  const timeRangeOptions: { value: TimeRange; label: string }[] = [
    { value: '4d', label: 'Last 4 Days' },
    { value: '3d', label: 'Last 3 Days' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'today', label: 'Today' },
  ]

  return (
    <div className={styles.page}>
      {/* Info Card */}
      {showInfo && (
        <div className={styles.infoCard}>
          <div className={styles.infoText}>
            This embedded dashboard shows real-time metrics from OpenSearch. Use the time range buttons below to adjust
            the view.
          </div>
          <button className={styles.closeBtn} onClick={dismissInfo} title="Dismiss forever">
            ✕
          </button>
        </div>
      )}

      {/* Controls */}
      <div className={styles.timeControls}>
        <div className={styles.rangeGroup}>
          <span className={styles.timeLabel}>Time Range:</span>
          {timeRangeOptions.map((option) => (
            <button
              key={option.value}
              className={`${styles.timeBtn} ${timeRange === option.value ? styles.active : ''}`}
              onClick={() => setTimeRange(option.value)}>
              {option.label}
            </button>
          ))}
        </div>

        <div className={styles.actionGroup}>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            Refresh
          </button>
          <a href={getFullUrl()} target="_blank" rel="noopener noreferrer" className={styles.openBtn}>
            Open in Kibana
          </a>
        </div>
      </div>

      {/* Iframe Container */}
      <div className={styles.iframeContainer}>
        {loading && (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <span>Loading dashboard...</span>
          </div>
        )}

        {error ? (
          <div className={styles.error}>
            <h3>⚠️ Unable to load Kibana dashboard</h3>
            <p>Make sure Kibana is running at {KIBANA_URL}</p>
          </div>
        ) : (
          <iframe
            key={refreshKey}
            src={getEmbedUrl()}
            className={styles.iframe}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title="Kibana Dashboard"
          />
        )}
      </div>
    </div>
  )
}

export default KibanaDashboard
